import { useEffect, useRef, useCallback, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@b2b/ui-components";
import { CountryPicker } from "../../components/CountryPicker";

import {
  User,
  Mail,
  Building,
  Globe,
  Phone,
  Lock,
  ArrowRightCircle,
  X,
  XCircle,
  Check,
  Loader2,
  Eye,
  EyeOff,
} from "lucide-react";

import { Link } from "react-router-dom";
import { useRegistrationForm, SyncRequest, type TradeRole } from "./hooks/useRegistrationForm";
import FormInput from "./components/FormInput";
import PasswordStrengthMeter from "./components/PasswordStrengthMeter";
import RegistrationHeader from "./components/RegistrationHeader";
import RoleSelector from "./components/RoleSelector";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    __recaptchaOnload?: () => void;
  }
}

export interface FirstLevelCategory {
  id: string;
  name: string;
}

export interface PricePlanOption {
  id: string;
  name: string;
}

interface RegistrationFormProps {
  onSyncUser?: (syncData: SyncRequest) => Promise<any>;
  onGetUserStatus?: () => Promise<{ status: string; user?: any }>;
  onRegister?: (
    data: SyncRequest & { password?: string; captchaToken?: string },
  ) => Promise<any>;
  onRegistrationSuccess?: (userRole: string) => void;
  onCheckShopNameAvailable?: (name: string) => Promise<{ available: boolean }>;
  auth0Email?: string;
  isDirectRegistration?: boolean;
  recaptchaSiteKey?: string;
  firstLevelCategories?: FirstLevelCategory[];
  /** Price plan options for seller signup (e.g. from membershipApi.getActivePlans()). */
  pricePlans?: PricePlanOption[];
  /** When true, form is rendered inside auth page (e.g. Sign Up tab); skips outer gradient wrapper and uses compact layout. */
  embeddedInAuthPage?: boolean;
  /** When provided (e.g. in embedded mode), "Already have an account?" uses this instead of navigating to /login. */
  onSwitchToSignIn?: () => void;
  /** When set (multi-step Step 2), role is fixed and RoleSelector is hidden. */
  initialRole?: TradeRole;
  /** Prefill email from multi-step Step 0 (email or OAuth path). */
  prefilledEmail?: string;
  /** When true (OAuth path), form uses onSyncUser/onGetUserStatus instead of onRegister. */
  isOAuthPath?: boolean;
}

const RECAPTCHA_SCRIPT_URL = "https://www.google.com/recaptcha/api.js";
const RECAPTCHA_ACTION = "register";

const RegistrationForm = (props: RegistrationFormProps = {}) => {
  const showRecaptcha = Boolean(
    props.isDirectRegistration && props.recaptchaSiteKey,
  );

  const getCaptchaToken = useCallback((): Promise<string | null> => {
    if (typeof window === "undefined" || !props.recaptchaSiteKey) return Promise.resolve(null);
    const sitekey = props.recaptchaSiteKey;
    return new Promise((resolve) => {
      if (!window.grecaptcha) {
        resolve(null);
        return;
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha!.execute(sitekey, { action: RECAPTCHA_ACTION })
          .then((token) => resolve(token || null))
          .catch(() => resolve(null));
      });
    });
  }, [props.recaptchaSiteKey]);

  const resetCaptcha = useCallback(() => {
    // v3: no widget to reset; each submit gets a fresh token via execute()
  }, []);

  useEffect(() => {
    if (!showRecaptcha || !props.recaptchaSiteKey) return;
    const sitekey = props.recaptchaSiteKey;
    if (document.querySelector(`script[src^="${RECAPTCHA_SCRIPT_URL}"]`)) return;
    const script = document.createElement("script");
    script.src = `${RECAPTCHA_SCRIPT_URL}?render=${sitekey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [showRecaptcha, props.recaptchaSiteKey]);

  const isOAuthPath = Boolean(props.isOAuthPath);
  const effectiveDirectRegistration = isOAuthPath ? false : props.isDirectRegistration;
  const auth0Email = props.prefilledEmail ?? props.auth0Email;

  const {
    tradeRole,
    setTradeRole,
    formData,
    handleInputChange,
    isLoading,
    handleSubmit,
    isFormComplete,
    submitError,
    clearSubmitError,
    shopNameStatus,
  } = useRegistrationForm({
    ...props,
    auth0Email,
    isDirectRegistration: effectiveDirectRegistration,
    getCaptchaToken: showRecaptcha ? getCaptchaToken : undefined,
    resetCaptcha: showRecaptcha ? resetCaptcha : undefined,
    initialRole: props.initialRole,
    prefilledEmail: props.prefilledEmail,
  });

  const isEmbedded = Boolean(props.embeddedInAuthPage);
  const onSwitchToSignIn = props.onSwitchToSignIn;
  const hideRoleSelector = props.initialRole != null;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formBody = (
    <>
              <CardContent className="w-full min-w-0 space-y-6 px-0 sm:px-2 md:space-y-8">
              {!hideRoleSelector && (
                <>
                  <RoleSelector tradeRole={tradeRole} onRoleChange={setTradeRole} />
                  <Separator />
                </>
              )}
              {hideRoleSelector && props.embeddedInAuthPage && (
                <div className="mb-2 w-full min-w-0">
                  <p className="text-sm font-medium text-gray-500">Step 2 of 2</p>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                    <div className="h-full w-full rounded-full bg-orange-500 transition-all" />
                  </div>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="w-full min-w-0 space-y-6"
                autoComplete="off"
              >
                <div
                  className={
                    hideRoleSelector
                      ? "w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50/40 p-3 sm:p-5 md:p-6"
                      : "w-full min-w-0"
                  }
                >
                  <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                  <FormInput
                    id="firstName"
                    name="registration-first-name"
                    label="First Name *"
                    icon={User}
                    type="text"
                    autoComplete="off"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    required
                  />
                  <FormInput
                    id="lastName"
                    name="registration-last-name"
                    label="Last Name *"
                    icon={User}
                    type="text"
                    autoComplete="off"
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    required
                  />
                  {/* Email: read-only in signup form (prefilled from step 0 or OAuth) */}
                  <div className="min-w-0 space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4 text-orange-500" />
                      Business Email{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <div
                      className="min-w-0 truncate rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700"
                      title={formData.email || undefined}
                    >
                      {formData.email || "Loading..."}
                    </div>
                    <p className="text-xs text-gray-500">
                      Email from your account
                    </p>
                  </div>
                  {(tradeRole === "supplier" || tradeRole === "both") && (
                    <div className="space-y-1.5">
                      <FormInput
                        id="company"
                        name="registration-shop-name"
                        label="Shop Name *"
                        icon={Building}
                        type="text"
                        autoComplete="off"
                        placeholder="Choose a unique shop name"
                        value={formData.company}
                        onChange={(e) =>
                          handleInputChange("company", e.target.value)
                        }
                        required
                      />
                      {props.onCheckShopNameAvailable && (
                        <div
                          className="flex items-center gap-2 text-sm min-h-[1.5rem]"
                          role="status"
                          aria-live="polite"
                        >
                          {shopNameStatus === "checking" && (
                            <>
                              <Loader2 className="w-4 h-4 text-gray-500 animate-spin flex-shrink-0" />
                              <span className="text-gray-500">
                                Checking availability...
                              </span>
                            </>
                          )}
                          {shopNameStatus === "available" && (
                            <>
                              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                              <span className="text-green-600">
                                Shop name is available
                              </span>
                            </>
                          )}
                          {shopNameStatus === "taken" && (
                            <>
                              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                              <span className="text-red-600">
                                This shop name is already taken
                              </span>
                            </>
                          )}
                          {shopNameStatus === "idle" &&
                            formData.company.trim() && (
                              <span className="text-gray-400">
                                Type to check if shop name is available
                              </span>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4 text-orange-500" />
                      Country/Region{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <CountryPicker
                      value={formData.country}
                      onValueChange={(value) =>
                        handleInputChange(
                          "country",
                          Array.isArray(value) ? value.join(", ") : value,
                        )
                      }
                      placeholder="Select your country"
                    />
                  </div>
                  <FormInput
                    id="mobile"
                    name="registration-mobile"
                    label="Mobile Number *"
                    icon={Phone}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="9876543210"
                    value={formData.mobile}
                    onChange={(e) =>
                      handleInputChange("mobile", e.target.value)
                    }
                    required
                  />
                  <FormInput
                    id="password"
                    name="new-password"
                    label="Password *"
                    icon={Lock}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    required
                    endAdornment={
                      <button
                        type="button"
                        className="rounded p-1 text-gray-400 hover:text-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        onClick={() => setShowPassword((p) => !p)}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    }
                  />
                  <div className="col-span-1 md:col-span-2">
                    <PasswordStrengthMeter password={formData.password} />
                  </div>
                  <FormInput
                    id="confirmPassword"
                    name="new-password-confirm"
                    label="Confirm Password *"
                    icon={Lock}
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    required
                    endAdornment={
                      <button
                        type="button"
                        className="rounded p-1 text-gray-400 hover:text-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        aria-label={
                          showConfirmPassword
                            ? "Hide confirm password"
                            : "Show confirm password"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    }
                    aria-invalid={
                      formData.confirmPassword.length > 0 &&
                      formData.password !== formData.confirmPassword
                    }
                    aria-describedby={
                      formData.confirmPassword.length > 0 &&
                      formData.password !== formData.confirmPassword
                        ? "confirmPassword-error"
                        : undefined
                    }
                  />
                  {formData.confirmPassword.length > 0 &&
                    formData.password !== formData.confirmPassword && (
                      <p
                        id="confirmPassword-error"
                        role="alert"
                        className="col-span-1 md:col-span-2 mt-1 text-sm text-red-600 font-medium"
                      >
                        Passwords do not match. Please try again.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex min-w-0 items-start gap-3 text-sm">
                  <Checkbox
                    id="terms"
                    aria-label="I agree to the Terms of Service and Privacy Policy"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) =>
                      handleInputChange("agreeToTerms", checked as boolean)
                    }
                    className="mt-1 shrink-0 border-neutral-500 dark:border-neutral-400"
                  />
                  <label
                    htmlFor="terms"
                    className="min-w-0 cursor-pointer text-sm font-medium leading-relaxed text-gray-700 select-none"
                  >
                    I agree to the{" "}
                    <span className="text-orange-500 hover:underline">
                      Terms of Service
                    </span>{" "}
                    and{" "}
                    <span className="text-orange-500 hover:underline">
                      Privacy Policy
                    </span>
                  </label>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="flex w-full min-w-0 items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800 sm:items-center sm:p-4"
                  >
                    <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 sm:mt-0" />
                    <p className="min-w-0 flex-1 break-words text-sm font-medium">
                      {submitError}
                    </p>
                    <button
                      type="button"
                      onClick={clearSubmitError}
                      className="ml-1 flex-shrink-0 transition-opacity hover:opacity-80 sm:ml-2"
                      aria-label="Close notification"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="flex w-full items-center justify-center gap-2 bg-orange-500 py-4 text-base font-semibold text-white hover:bg-orange-700 sm:py-5 sm:text-lg md:py-6"
                  disabled={!isFormComplete || isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                      Creating Account...
                    </div>
                  ) : (
                    <>
                      <ArrowRightCircle className="w-5 h-5" />
                      Create Account
                    </>
                  )}
                </Button>
              </form>

              <div className="text-center pt-4 mt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Already have an account?{" "}
                  {onSwitchToSignIn ? (
                    <button
                      type="button"
                      onClick={onSwitchToSignIn}
                      className="text-orange-600 hover:text-orange-700 font-semibold"
                    >
                      Sign in
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className="text-orange-600 hover:text-orange-700 font-semibold"
                    >
                      Sign in
                    </Link>
                  )}
                </p>
              </div>
            </CardContent>
    </>
  );

  if (isEmbedded) {
    return (
      <div className="relative w-full min-w-0">
        {isLoading && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/90 px-4 py-12 text-center"
            aria-live="polite"
            role="status"
            aria-busy="true"
          >
            <Loader2 className="h-9 w-9 animate-spin text-orange-500 sm:h-10 sm:w-10" />
            <p className="mt-3 text-sm font-medium text-gray-700 sm:text-base">
              Creating account...
            </p>
          </div>
        )}
        <div className="mb-5 min-w-0 px-1 text-center sm:mb-6">
          <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
            Create Your Business Account
          </h3>
          <p className="mt-1 text-xs text-gray-600 sm:text-sm">
            Join millions of businesses trading globally
          </p>
        </div>
        {formBody}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 via-white to-orange-100 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto relative">
          {isLoading && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/90 py-20"
              aria-live="polite"
              role="status"
              aria-busy="true"
            >
              <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
              <p className="mt-4 text-gray-700 font-medium">
                Creating account...
              </p>
              <p className="mt-1 text-sm text-gray-500">Please wait</p>
            </div>
          )}
          <Card className="shadow-lg border-0 bg-white">
            <RegistrationHeader />
            {formBody}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
